import { FocusEvent, useCallback, useEffect, useState } from 'react';
import Script from 'next/script';
import EditingHelpText, { MissingDataSource } from 'components/helpers/EditingHelpText';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import {
  type SocialEmbedProps,
  buildEmbedConfig,
  buildLinkedInEmbedUrl,
} from 'lib/util/social-embed';
import SummaryPanel from './_SummaryPanel';
import { classnames } from 'tailwindcss-classnames';

declare global {
  interface Window {
    instgrm?: {
      Embeds?: {
        process?: () => void;
      };
    };
  }
}

export const SocialEmbed = ({ fields }: SocialEmbedProps): JSX.Element => {
  const isExperienceEditor = useExperienceEditor();
  const embedConfig = buildEmbedConfig({ fields });
  const [isSummaryPanelVisible, setSummaryPanelVisible] = useState(false);

  type ExperienceEditorWindow = Window & {
    top: Window & {
      Sitecore?: {
        PageModes?: {
          ChromeManager?: {
            resetChromes: () => void;
          };
        };
      };
    };
  };

  useEffect(() => {
    if (!embedConfig.isValid || !embedConfig.provider) {
      return;
    }
    if (typeof window !== 'undefined') {
      (window as ExperienceEditorWindow).top?.Sitecore?.PageModes?.ChromeManager?.resetChromes();
    }
    if (embedConfig.provider === 'instagram') {
      window.instgrm?.Embeds?.process?.();
    }
  }, [embedConfig.isValid, embedConfig.provider, embedConfig.url]);

  const showSummaryPanel = useCallback(() => {
    if (isExperienceEditor) {
      setSummaryPanelVisible(true);
    }
  }, [isExperienceEditor]);

  const hideSummaryPanel = useCallback(() => {
    if (isExperienceEditor) {
      setSummaryPanelVisible(false);
    }
  }, [isExperienceEditor]);

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      hideSummaryPanel();
      if (typeof window !== 'undefined') {
        (window as ExperienceEditorWindow).top?.Sitecore?.PageModes?.ChromeManager?.resetChromes();
      }
    }
  };

  const interactiveContainerProps = isExperienceEditor
    ? {
        onMouseEnter: showSummaryPanel,
        onMouseLeave: hideSummaryPanel,
        onFocusCapture: showSummaryPanel,
        onBlurCapture: handleBlur,
        onMouseDown: showSummaryPanel,
      }
    : {};

  if (!fields) {
    return <MissingDataSource />;
  }

  const summaryPanel = isExperienceEditor ? (
    <SummaryPanel
      isVisible={isSummaryPanelVisible}
      provider={embedConfig.provider}
      fields={fields}
    />
  ) : null;

  if (!embedConfig.isValid || !embedConfig.provider) {
    if (!isExperienceEditor) {
      return <></>;
    }

    const editingErrorText = embedConfig.hasExplicitNetwork
      ? 'Social Embed | Enter a valid social post URL for the selected network.'
      : 'Social Embed | Auto-detect could not determine a network. Select a network or update the URL.';

    return (
      <div
        data-component="authorable/social/social-embed"
        {...interactiveContainerProps}
        className={classnames('w-full')}
      >
        {summaryPanel}
        <EditingHelpText text={editingErrorText} block />
      </div>
    );
  }

  const instagramCaptionAttributes = embedConfig.showCaption
    ? { 'data-instgrm-captioned': 'true' }
    : {};

  /*
  Disabling TikTok support due to business request.
  const tiktokVideoIdMatch = embedConfig.url.match(/\/video\/(\d+)/i);
  const tiktokAttributes = tiktokVideoIdMatch?.[1]
    ? { 'data-video-id': tiktokVideoIdMatch[1] }
    : {};
  */
  const facebookSrc = `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(
    embedConfig.url
  )}&show_text=${embedConfig.showCaption ? 'true' : 'false'}&width=500`;

  const linkedInEmbedUrl = buildLinkedInEmbedUrl(new URL(embedConfig.url));

  return (
    <div
      data-component="authorable/social/social-embed"
      {...interactiveContainerProps}
      className={classnames('w-full')}
    >
      {summaryPanel}
      {embedConfig.provider === 'instagram' && (
        <Script
          id="social-embed-instagram-sdk"
          src="https://www.instagram.com/embed.js"
          strategy="afterInteractive"
          onLoad={() => {
            window.instgrm?.Embeds?.process?.();
          }}
        />
      )}

      {/* Disabling TikTok support due to business request. 
      {embedConfig.provider === 'tiktok' && (
        <Script
          id="social-embed-tiktok-sdk"
          src="https://www.tiktok.com/embed.js"
          strategy="afterInteractive"
        />
      )}
      */}

      {embedConfig.provider === 'instagram' && (
        <blockquote
          className="instagram-media w-full"
          data-instgrm-permalink={embedConfig.url}
          data-instgrm-version="14"
          {...instagramCaptionAttributes}
        >
          <a href={embedConfig.url} target="_blank" rel="noreferrer noopener">
            View this post on Instagram
          </a>
        </blockquote>
      )}

      {embedConfig.provider === 'facebook' && (
        <iframe
          src={facebookSrc}
          title={embedConfig.title}
          width="500"
          height={embedConfig.showCaption ? '690' : '580'}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          className={classnames('w-full')}
          referrerPolicy="strict-origin-when-cross-origin"
        />
      )}

      {embedConfig.provider === 'linkedin' && linkedInEmbedUrl && (
        <iframe
          src={linkedInEmbedUrl}
          title={embedConfig.title}
          width="504"
          height="620"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className={classnames('w-full')}
        />
      )}

      {embedConfig.provider === 'linkedin' && !linkedInEmbedUrl && isExperienceEditor && (
        <EditingHelpText text="Social Embed | LinkedIn URL must be an embeddable post URL." block />
      )}

      {/* embedConfig.provider === 'tiktok' && (
        <blockquote className="tiktok-embed w-full" cite={embedConfig.url} {...tiktokAttributes}>
          <section>
            <a href={embedConfig.url} target="_blank" rel="noreferrer noopener">
              View this post on TikTok
            </a>
          </section>
        </blockquote>
      ) */}
    </div>
  );
};

export default SocialEmbed;
